import { toast } from "react-toastify";
import { Dropdown } from "react-bootstrap";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import SiteDropdown from "@/components/Dropdown";
import DashboardModal from "./DashboardModal";
import useCollaboratorInvite from "@/hooks/useCollaboratorInvite";
import type { CollaboratorType } from "@/types";
import Spinner from "../Spinner";
import "@/styles/invite_collaborator.scss";

interface Props {
	onClose: () => void;
	eventName: string;
	collaboratorDetails: CollaboratorType;
}

type roleOptionsType = "Groom 🤵🏻‍♂️" | "Bride 👰" | "Planner 👩🏻‍💻" | "Lead planner 🕵️‍♂️" | "Coordinator 👷‍♀️" | "Relative 👫🏻" | "Other 🧐";
const roleOptions: Array<roleOptionsType> =
["Groom 🤵🏻‍♂️", "Bride 👰", "Planner 👩🏻‍💻", "Lead planner 🕵️‍♂️", "Coordinator 👷‍♀️", "Relative 👫🏻", "Other 🧐"];
const collaboratorPermission = [
    { name: "can_edit", label: "Can Edit" },
    { name: "view_only", label: "View Only" },
];

/**
 * InviteCollaboratorModal: Invite a collaborator to an event
 * @param eventName
 * @param onClose
 * @returns JSX
 */

export default function EditCollaboratorModal({ onClose, collaboratorDetails, eventName }: Props) {
    const [collaborator, setCollaborator] = useState({
        name: collaboratorDetails?.name ?? "",
        email: collaboratorDetails.email,
        role: collaboratorDetails.role,
        permission: collaboratorDetails.permission ?? "",
    });
    const { inviteCollaboratorRequest, loading } = useCollaboratorInvite();
    const queryClient = useQueryClient();

    function onSelectCollaboratorRoleHandler(role: string) {
        setCollaborator({
            ...collaborator,
            role,
        });
    }

    function onSelectCollaboratorPermissionHandler(permission: string) {
        setCollaborator({
            ...collaborator,
            permission,
        });
    }

    const selectedCollaboratorPermission = collaboratorPermission.filter((item) => item.name === collaborator.permission);

    function inviteCollaboratorSuccess() {
        setCollaborator({
            name: "",
            email: "",
            role: "Planner",
            permission: "can_edit",
        });
        queryClient.resetQueries();
        onClose();
    }

    async function inviteCollaboratorHandler() {
        try {
            return toast.info("Feature in progress");
            await inviteCollaboratorRequest(collaborator, inviteCollaboratorSuccess);
        } catch (error) {
            console.log("error", error);
        }
    }

    return (
        <DashboardModal
            className="invite_collaborator_modal"
            title={`Edit ${collaborator.name}'s Access`}
            description={`Edit collaborator's access to ${eventName} event`}
            onClose={onClose}>
            <section className="invite_modal_content">
                <div className="top_row">
                    <div className="action_button_group">
                        <SiteDropdown className="custom add_role" title={collaborator.role ? collaborator.role : "Role"}>
                            {roleOptions.map((role, index) => (
                                <Dropdown.Item key={index} type="button" onClick={() => onSelectCollaboratorRoleHandler(role)}>
                                    {role}
                                </Dropdown.Item>
                            ))}
                        </SiteDropdown>
                        <SiteDropdown className="custom permission" title={selectedCollaboratorPermission[0].label}>
                            {collaboratorPermission.map((permission, index) => (
                                <Dropdown.Item
                                    key={index}
                                    type="button"
                                    className="permission"
                                    onClick={() => onSelectCollaboratorPermissionHandler(permission.name)}>
                                    {permission.label}
                                </Dropdown.Item>
                            ))}
                        </SiteDropdown>
                    </div>
                    <div className="button_group">
                        <button type="button" className="invite_btn" onClick={inviteCollaboratorHandler}>
							Update
                            {loading && (
                                <span>
                                    <Spinner size={20} />
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </section>
        </DashboardModal>
    );
}
